import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from '@mui/material'
import * as Sentry from '@sentry/react'
import { debounce } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import gsAPI from '../../../API/gsConfig'
import { ChallengeWrapper, UserInfo } from '../../../components/matchmaking/ChallengeCard'
import { Dialogs, useDialogs } from '../../../sharedComponents/src/Contexts/DialogsContext'
import JoinUs from '../../../sharedComponents/src/Dialogs/Forms/JoinUs'
import { createQueryParams } from '../../../sharedComponents/src/globalHeader/common/queryParams'
import { errorHandler } from '../../../sharedComponents/src/globalHeader/functions/ams-functions'
import { Rules } from '../../../sharedComponents/src/globalHeader/GlobalHeader'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import { LiveGameData } from '../../../store/types'

let reportedNonArrayMatches = false

export async function getMatchesInProgress(
    token: string,
    limit: number,
    offset: number,
    gameType: string,
    filter?: string,
): Promise<any> {
    try {
        const response = await gsAPI.get(
            `/api/games_in_progress${createQueryParams({
                gameType,
                filter,
                limit,
                offset,
                token,
            })}`,
        )

        if (!Array.isArray(response.data)) {
            // polled every 5 s: report the unexpected shape once per page load
            if (!reportedNonArrayMatches) {
                reportedNonArrayMatches = true
                Sentry.captureMessage('games_in_progress returned a non-array body', {
                    level: 'warning',
                    extra: { type: response.data === null ? 'null' : typeof response.data },
                })
            }
            return []
        }
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export interface ObserveTabProps {
    liveGame: boolean
}

const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'bullet', label: 'Bullet' },
    { value: 'blitz', label: 'Blitz' },
    { value: 'rapid', label: 'Rapid' },
    { value: 'classic', label: 'Classic' },
    { value: 'follow', label: 'Following' },
]

export const ObserveTab = (props: ObserveTabProps) => {
    const token = useStoreState((state) => state.token)
    const rules = useStoreState((state) => state.rules)
    const sendSubscribe = useStoreActions((state) => state.observeView.sendSubscribe)
    const setActiveTab = useStoreActions((state) => state.observeView.setActiveTab)
    const getScore = useStoreActions((state) => state.matchMaker.getScore)
    const [matches, setMatches] = useState([] as LiveGameData[])
    const [isLiveGamesLoading, setIsLiveGamesLoading] = useState<boolean>(true)
    const { registerDialog } = useDialogs()

    const liveGamesEnabled = rules.some((rule) => rule === Rules.FULL_ACCESS || rule === Rules.LIVE_GAMES)
    const followEnabled = rules.some((rule) => rule === Rules.FULL_ACCESS || rule === Rules.FOLLOW)

    const [gameType, setGameType] = useState('all')
    let limit = 50
    const [offset, setOffset] = useState(0)

    const fetchMatchesInProgress = useCallback(
        // debounce to prevent double fetch
        debounce(async () => {
            if (!token) return
            let requestData = { gameType: gameType, filter: '' }
            if (gameType === 'follow') {
                requestData.gameType = 'all'
                requestData.filter = gameType
            }
            const data = await getMatchesInProgress(token, limit, offset, requestData.gameType, requestData.filter)

            if (data.error || !data) {
                setMatches([])
            } else {
                setMatches(data)
            }
            setIsLiveGamesLoading(false)
        }, 10),
        [token, limit, offset, gameType],
    )

    useEffect(() => {
        const intervalId = setInterval(() => {
            fetchMatchesInProgress()
        }, 5000)
        return () => clearInterval(intervalId)
    }, [fetchMatchesInProgress, token])

    useEffect(() => {
        fetchMatchesInProgress()
    }, [gameType, offset])

    const onShowMoreClick = () => {
        setOffset(offset + 10)
    }

    const onShowLessClick = () => {
        setOffset(offset - 10)
    }

    const handleTypeSelect = (e) => {
        if (e.target.value === 'follow' && !followEnabled) {
            registerDialog(Dialogs.JOIN_US, <JoinUs />)
            return
        }

        setGameType(e.target.value)
        setOffset(0)
    }

    const onLiveGameClick = (game: LiveGameData) => {
        if (!liveGamesEnabled) {
            registerDialog(Dialogs.JOIN_US, <JoinUs />)
        } else {
            sendSubscribe({
                matchId: game.matchId,
                liveGameData: game,
            })
            getScore({ challengerId: game.player1.playerId, challengeeId: game.player2.playerId })
            setActiveTab(1)
        }
    }

    return (
        <Paper
            square
            sx={{
                border: 'solid',
                display: 'flex',
                flexGrow: 1,
                minHeight: '250px',
                flexFlow: 'column',
            }}
        >
            <FormControl fullWidth sx={{ marginTop: 1 }}>
                <InputLabel sx={{ ml: 1 }} variant="standard" id="sort-select-label">
                    Sort By Game:
                </InputLabel>
                <Select
                    labelId="sort-select-label"
                    sx={{ textTransform: 'capitalize' }}
                    defaultValue={gameType}
                    value={gameType}
                    variant="standard"
                    onChange={handleTypeSelect}
                >
                    {filterOptions.map((item) => (
                        <MenuItem
                            sx={{ fontFamily: 'Space Grotesk', textTransform: 'capitalize' }}
                            key={item.value}
                            value={item.value}
                        >
                            {item.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {isLiveGamesLoading ? (
                <Stack gap={1} sx={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress style={{ width: '100px', height: '100px' }} />
                </Stack>
            ) : (
                <Box overflow="auto" flexGrow={1} minHeight="0" sx={{ flex: 1, overflowY: 'auto' }}>
                    {matches.map((game: LiveGameData) => (
                        <ChallengeWrapper
                            key={game.matchId}
                            type="view"
                            handleAction={() => onLiveGameClick(game)}
                            game={game}
                            ratingType={game.timeMode.name}
                        >
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="flex-end"
                                flexGrow={1}
                                minWidth={'100px'}
                            >
                                <UserInfo
                                    type="view"
                                    direction="row"
                                    player={{
                                        ...game.player1,
                                        botdRewardUrl: undefined,
                                        username: game.player1.userName,
                                        rating: !!game.player1.ratings
                                            ? game.player1.ratings[game.timeMode.name].rating
                                            : 0,
                                        is_provisional: !!game.player1.ratings
                                            ? game.player1.ratings[game.timeMode.name].is_provisional
                                            : false,
                                        color: game.player1.playerColor,
                                        nationality: game.player1.countryId || 'WORLD',
                                        reward: {
                                            tier: game.player1.reward?.tier,
                                            season: game.player1.reward?.season,
                                        },
                                    }}
                                    ratingType={game.timeMode.name}
                                />

                                <UserInfo
                                    type="view"
                                    direction="row"
                                    player={{
                                        ...game.player2,
                                        botdRewardUrl: undefined,
                                        username: game.player2.userName,
                                        rating: !!game.player2.ratings
                                            ? game.player2.ratings[game.timeMode.name].rating
                                            : 0,
                                        is_provisional: !!game.player2.ratings
                                            ? game.player2.ratings[game.timeMode.name].is_provisional
                                            : false,
                                        color: game.player2.playerColor,
                                        nationality: game.player2.countryId || 'WORLD',
                                        reward: {
                                            tier: game.player2.reward?.tier,
                                            season: game.player2.reward?.season,
                                        },
                                    }}
                                    ratingType={game.timeMode.name}
                                />
                            </Box>
                        </ChallengeWrapper>
                    ))}
                    {matches.length === 0 && (
                        <Typography sx={{ textAlign: 'center', py: 2 }}>No Active Games</Typography>
                    )}
                </Box>
            )}

            <Box
                justifyContent="center"
                alignItems="center"
                gap={1}
                display={offset <= 0 || matches === undefined || matches.length < limit ? 'none' : 'flex'}
            >
                <Button
                    sx={{ justifyContent: 'flex-start' }}
                    size="small"
                    fullWidth
                    variant="text"
                    onClick={onShowLessClick}
                    disabled={offset <= 0}
                    startIcon={<ArrowBackIosNewIcon />}
                >
                    Previous
                </Button>
                <Button
                    sx={{ justifyContent: 'flex-end' }}
                    size="small"
                    fullWidth
                    variant="text"
                    onClick={onShowMoreClick}
                    disabled={matches === undefined || matches.length < limit}
                    endIcon={<ArrowForwardIosIcon />}
                >
                    Next
                </Button>
            </Box>
        </Paper>
    )
}
